import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AttackFlowStep } from "@/types/security";
import { getRiskBadgeHexColor } from "@/utils/securityHelpers";
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Shield,
  User,
  Globe,
  Server,
  Database,
  Lock,
  Brain,
  Container,
  Network,
  AlertTriangle,
  FileText,
  Cpu,
  Cloud,
} from "lucide-react";

interface AttackGraphVisualProps {
  attackFlow: AttackFlowStep[];
}

interface NodePosition {
  x: number;
  y: number;
}

export function AttackGraphVisual({ attackFlow }: AttackGraphVisualProps) {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  // Calculate node positions in a horizontal flow layout
  const getNodePositions = (): Map<string, NodePosition> => {
    const positions = new Map<string, NodePosition>();
    const startX = 120;
    const centerY = 200;
    const horizontalSpacing = 180;

    attackFlow.forEach((flow, index) => {
      // Add some vertical variation for visual interest
      const yOffset = index % 2 === 0 ? -20 : 20;
      positions.set(flow.id, {
        x: startX + index * horizontalSpacing,
        y: centerY + yOffset,
      });
    });

    return positions;
  };

  const nodePositions = getNodePositions();

  // Get icon component based on component name
  const getComponentIcon = (componentName: string) => {
    const name = componentName.toLowerCase();
    if (name.includes("user") || name.includes("input")) return User;
    if (name.includes("api") || name.includes("gateway")) return Server;
    if (name.includes("agent") || name.includes("assistant")) return Brain;
    if (name.includes("guard") || name.includes("scanner") || name.includes("detection"))
      return Shield;
    if (name.includes("model") || name.includes("llm") || name.includes("gpt")) return Cpu;
    if (name.includes("database") || name.includes("data")) return Database;
    if (name.includes("internet") || name.includes("web")) return Globe;
    if (name.includes("network")) return Network;
    if (name.includes("container") || name.includes("docker")) return Container;
    if (name.includes("cloud")) return Cloud;
    if (name.includes("anonymizer") || name.includes("validator")) return Lock;
    if (name.includes("output")) return FileText;
    return AlertTriangle; // default
  };

  // Node color configuration (professional, enterprise styling)
  const getNodeColor = (status: string) => {
    switch (status) {
      case "protected":
        return {
          bg: "#ffffff",
          border: "#10b981",
          borderWidth: "3",
          text: "#064e3b",
          iconColor: "#10b981",
          statusBg: "#d1fae5",
        };
      case "vulnerable":
        return {
          bg: "#ffffff",
          border: "#ef4444",
          borderWidth: "3",
          text: "#7f1d1d",
          iconColor: "#ef4444",
          statusBg: "#fee2e2",
        };
      case "warning":
        return {
          bg: "#ffffff",
          border: "#f59e0b",
          borderWidth: "3",
          text: "#78350f",
          iconColor: "#f59e0b",
          statusBg: "#fef3c7",
        };
      default:
        return {
          bg: "#ffffff",
          border: "#6b7280",
          borderWidth: "2",
          text: "#374151",
          iconColor: "#6b7280",
          statusBg: "#f3f4f6",
        };
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === svgRef.current || (e.target as HTMLElement).tagName === "svg") {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Attack Path Visualization
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Interactive graph showing the attack flow through your system
            </p>
          </div>
          {/* Zoom Controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleReset}
              title="Reset View"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className="relative bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm"
          style={{ height: "450px" }}
        >
          {/* SVG Canvas */}
          <svg
            ref={svgRef}
            className="w-full h-full cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <defs>
              {/* Subtle drop shadow for depth */}
              <filter id="drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                <feOffset dx="0" dy="1" result="offsetblur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.15" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* Subtle glow for selected nodes */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.3" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* Arrow marker */}
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <polygon points="0 0, 10 3, 0 6" fill="currentColor" />
              </marker>
            </defs>

            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Draw connections first (so they appear behind nodes) */}
              {attackFlow.map((flow, index) => {
                if (index === attackFlow.length - 1) return null;
                const currentPos = nodePositions.get(flow.id)!;
                const nextPos = nodePositions.get(attackFlow[index + 1].id)!;
                const riskColor = getRiskBadgeHexColor(attackFlow[index + 1].risk);

                return (
                  <g key={`edge-${flow.id}`}>
                    {/* Professional connection line */}
                    <line
                      x1={currentPos.x + 50}
                      y1={currentPos.y}
                      x2={nextPos.x - 50}
                      y2={nextPos.y}
                      stroke="#d1d5db"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="none"
                      markerEnd="url(#arrowhead)"
                      opacity="0.5"
                    />
                  </g>
                );
              })}

              {/* Draw nodes */}
              {attackFlow.map((flow) => {
                const pos = nodePositions.get(flow.id)!;
                const colors = getNodeColor(flow.status);
                const isSelected = selectedNode === flow.id;

                return (
                  <g
                    key={flow.id}
                    transform={`translate(${pos.x}, ${pos.y})`}
                    onClick={() => setSelectedNode(isSelected ? null : flow.id)}
                    className="cursor-pointer transition-all"
                    filter={isSelected ? "url(#glow)" : "url(#drop-shadow)"}
                  >
                    {/* Selection ring - subtle professional style */}
                    {isSelected && (
                      <circle
                        r="52"
                        fill="none"
                        stroke={colors.border}
                        strokeWidth="2"
                        opacity="0.2"
                      />
                    )}

                    {/* Node circle - clean white background */}
                    <circle
                      r="45"
                      fill={colors.bg}
                      stroke={colors.border}
                      strokeWidth={colors.borderWidth}
                    />

                    {/* Status indicator ring */}
                    <circle
                      r="42"
                      fill="none"
                      stroke={colors.statusBg}
                      strokeWidth="6"
                      opacity="0.4"
                    />

                    {/* Step number badge - modern floating badge */}
                    <g transform="translate(-30, -30)">
                      <circle
                        r="14"
                        fill="white"
                        stroke={colors.border}
                        strokeWidth="2.5"
                        filter="url(#drop-shadow)"
                      />
                      <text
                        textAnchor="middle"
                        y="5"
                        fontSize="13"
                        fontWeight="700"
                        fill={colors.border}
                      >
                        {flow.step}
                      </text>
                    </g>

                    {/* Icon - Render the component icon */}
                    <g transform="translate(-12, -20)">
                      <foreignObject width="24" height="24">
                        {(() => {
                          const IconComponent = getComponentIcon(flow.component);
                          return (
                            <IconComponent
                              className="w-6 h-6"
                              style={{ color: colors.iconColor, strokeWidth: 1.5 }}
                            />
                          );
                        })()}
                      </foreignObject>
                    </g>

                    {/* Component name - professional typography */}
                    <text
                      y="10"
                      textAnchor="middle"
                      fontSize="10"
                      fontWeight="600"
                      fill={colors.text}
                      className="pointer-events-none"
                    >
                      {flow.component.length > 12
                        ? flow.component.substring(0, 12) + "..."
                        : flow.component}
                    </text>

                    {/* Risk badge - subtle professional style */}
                    <g transform="translate(0, 25)">
                      <rect
                        x="-28"
                        y="0"
                        width="56"
                        height="16"
                        rx="8"
                        fill={colors.border}
                        opacity="0.15"
                      />
                      <text
                        textAnchor="middle"
                        y="11"
                        fontSize="8"
                        fontWeight="600"
                        fill={colors.border}
                        className="pointer-events-none"
                        letterSpacing="0.3"
                      >
                        {flow.risk.toUpperCase()}
                      </text>
                    </g>

                    {/* Hover tooltip - modern card */}
                    {isSelected && (
                      <g transform={`translate(${pos.x > 600 ? -310 : 60}, -50)`}>
                        <rect
                          width="300"
                          height="90"
                          rx="12"
                          fill="white"
                          stroke="#e5e7eb"
                          strokeWidth="2"
                          filter="url(#drop-shadow)"
                        />
                        <rect width="300" height="30" rx="12" fill={colors.border} opacity="0.1" />
                        <text x="15" y="20" fontSize="13" fontWeight="700" fill="#111827">
                          {flow.component}
                        </text>
                        <text x="15" y="50" fontSize="11" fill="#6b7280" fontWeight="500">
                          {flow.action.length > 50
                            ? flow.action.substring(0, 50) + "..."
                            : flow.action}
                        </text>
                        <g transform="translate(15, 65)">
                          <rect width="60" height="16" rx="8" fill="#f3f4f6" />
                          <text
                            x="30"
                            y="11"
                            textAnchor="middle"
                            fontSize="9"
                            fontWeight="600"
                            fill="#374151"
                          >
                            {flow.status.toUpperCase()}
                          </text>
                        </g>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {/* Legend */}
          <div className="absolute bottom-4 right-4 bg-white rounded-lg border-2 border-gray-200 p-3 shadow-lg">
            <div className="text-xs font-semibold mb-2">Legend</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Protected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span>Warning</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>Vulnerable</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2 pt-2 border-t">Click nodes for details</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
